import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState, type ReactElement, type ReactNode } from 'react'

import {
  ActivityTab,
  AddonsManager,
  ArchiveTeamDialog,
  ConsumptionCard,
  MembersTab,
  OverviewTab,
  PaygCard,
  RateLimitForm,
  SeatEditorDialog,
  StorageCard,
  TeamDetailLayout,
  teamDetailQuery,
  useAdjustTeamSeatsMutation,
  useArchiveTeamMutation,
  useRestoreTeamMutation,
  type TeamDetailTabId,
} from '~/features/teams'
import { TeamAuditTab } from '~/features/teams/components/tabs/TeamAuditTab'
import { SubscriptionTab } from '~/features/teams/components/tabs/SubscriptionTab'
import { DangerZoneTab } from '~/features/teams/components/tabs/DangerZoneTab'
import { PaymentsTab } from '~/features/billing'
import type { AppError } from '~/lib/result'

/**
 * `/admin/teams/:teamId` — detail route.
 *
 * Owns the tab state + dialog state. Each tab fetches its own data via
 * its own `useQuery` (StorageCard, ConsumptionCard, RateLimitForm,
 * PaygCard, AddonsManager, TeamAuditTab all do this internally), so a
 * slow / failed sub-resource never blanks the rest of the page.
 *
 * The legacy `buildSeedEntitlements()` helper that backed the old Modules
 * tab is gone — the Modules tab now renders the canonical AddonsManager.
 */
export const Route = createFileRoute('/admin/teams/$teamId')({
  component: TeamDetailPage,
})

type DialogKind = { readonly kind: 'archive' } | { readonly kind: 'seats' } | null

type ExtendedTabId =
  | TeamDetailTabId
  | 'addons'
  | 'subscription'
  | 'payments'
  | 'storage'
  | 'consumption'
  | 'rate-limits'
  | 'danger-zone'

function TeamDetailPage(): ReactElement {
  const { teamId } = Route.useParams()
  const detailQuery = useQuery(teamDetailQuery(teamId))
  const archiveMutation = useArchiveTeamMutation(teamId)
  const restoreMutation = useRestoreTeamMutation(teamId)
  const seatsMutation = useAdjustTeamSeatsMutation(teamId)
  const [dialog, setDialog] = useState<DialogKind>(null)
  const [tab, setTab] = useState<ExtendedTabId>('overview')

  const apiError = detailQuery.error as AppError | null
  const isPermissionDenied =
    apiError?.code === 'forbidden' || apiError?.code === 'unauthorized'
  const isNotFound = apiError?.code === 'not_found'

  if (detailQuery.isLoading) {
    return <DetailSkeleton />
  }

  if (isPermissionDenied) {
    return (
      <PageMessage
        title="You don't have access"
        body="Viewing team details requires platform-admin permission."
      />
    )
  }

  if (isNotFound || detailQuery.isError || detailQuery.data === undefined) {
    return (
      <PageMessage
        title="Team not found"
        body="The team you requested doesn't exist, was archived and purged, or you don't have access to it."
      />
    )
  }

  const detail = detailQuery.data
  const isArchived = detail.team.archived_at !== null

  function openArchive(): void {
    setDialog({ kind: 'archive' })
  }
  function openSeats(): void {
    setDialog({ kind: 'seats' })
  }

  async function submitArchive(input: {
    readonly reason: string | undefined
  }): Promise<void> {
    await archiveMutation.mutateAsync(input)
    setDialog(null)
  }

  async function submitSeats(input: {
    readonly seats: number
    readonly isAdminGranted: boolean
    readonly notes: string | undefined
  }): Promise<void> {
    await seatsMutation.mutateAsync(input)
    setDialog(null)
  }

  // Render the page through `TeamDetailLayout` for the legacy 5 tabs
  // (Overview / Members / Activity / Audit). New surfaces (Add-ons,
  // Subscription, Storage, Consumption, Rate Limits, Danger Zone) live
  // under a sibling tab bar to keep this PR additive.
  // TODO: collapse the dual bars into one when the layout is widened to
  // accept arbitrary tab specs.
  return (
    <div className="ka-page">
      <TeamDetailLayout
        detail={detail}
        activeTab={isTeamDetailTab(tab) ? tab : null}
        onTabChange={(next) => setTab(next)}
        headerExtras={
          <>
            {isArchived ? (
              <button
                type="button"
                className="ka-btn ka-btn--secondary ka-btn--sm"
                onClick={() => restoreMutation.mutate()}
                disabled={restoreMutation.isPending}
              >
                {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
              </button>
            ) : (
              <button
                type="button"
                className="ka-btn ka-btn--ghost ka-btn--sm"
                onClick={openArchive}
              >
                Archive team
              </button>
            )}
          </>
        }
        renderOverview={() => (
          <OverviewTab
            detail={detail}
            onOpenSeatEditor={
              detail.team.organization_id !== null ? openSeats : undefined
            }
          />
        )}
        renderMembers={() => <MembersTab detail={detail} />}
        renderModules={() => <AddonsManager scope="team" id={teamId} />}
        renderActivity={() => <ActivityTab teamId={teamId} />}
        renderAudit={() => <TeamAuditTab teamId={teamId} />}
      />

      {/* Extended admin surfaces (B5/B6 of the rollup plan). */}
      <ExtendedTabsBar tab={tab} onTabChange={setTab} />
      <div role="tabpanel" id={`team-extended-tabpanel-${tab}`}>
        {tab === 'addons' ? (
          <AddonsManager scope="team" id={teamId} />
        ) : null}
        {tab === 'subscription' ? (
          <>
            <SubscriptionTab
              teamId={teamId}
              subscriptionSummary={detail.subscription ?? undefined}
            />
            <div className="ka-page-section"><PaygCard teamId={teamId} /></div>
          </>
        ) : null}
        {tab === 'payments' ? (
          <PaymentsTab entityType="team" entityId={teamId} entityName={detail.team.name} />
        ) : null}
        {tab === 'storage' ? <StorageCard teamId={teamId} /> : null}
        {tab === 'consumption' ? <ConsumptionCard teamId={teamId} /> : null}
        {tab === 'rate-limits' ? <RateLimitForm teamId={teamId} /> : null}
        {tab === 'danger-zone' ? (
          <DangerZoneTab
              teamId={teamId}
              teamName={detail.team.name}
              currentType={detail.team.team_type}
              archived={isArchived}
              onArchiveClick={openArchive}
            />
        ) : null}
      </div>

      {dialog?.kind === 'archive' ? (
        <ArchiveTeamDialog
          teamName={detail.team.name}
          memberCount={detail.member_count}
          onClose={() =>
            archiveMutation.isPending ? undefined : setDialog(null)
          }
          onSubmit={submitArchive}
          isSubmitting={archiveMutation.isPending}
          errorMessage={
            archiveMutation.error instanceof Error
              ? archiveMutation.error.message
              : undefined
          }
        />
      ) : null}

      {dialog?.kind === 'seats' ? (
        <SeatEditorDialog
          entityName={detail.team.name}
          entityKind="team"
          currentSeats={detail.team.allocated_seats ?? 0}
          onClose={() =>
            seatsMutation.isPending ? undefined : setDialog(null)
          }
          onSubmit={submitSeats}
          isSubmitting={seatsMutation.isPending}
          errorMessage={
            seatsMutation.error instanceof Error
              ? seatsMutation.error.message
              : undefined
          }
        />
      ) : null}
    </div>
  )
}

function isTeamDetailTab(tab: ExtendedTabId): tab is TeamDetailTabId {
  return (
    tab === 'overview' ||
    tab === 'members' ||
    tab === 'modules' ||
    tab === 'activity' ||
    tab === 'audit'
  )
}

interface ExtendedTabsBarProps {
  readonly tab: ExtendedTabId
  readonly onTabChange: (next: ExtendedTabId) => void
}

const EXTENDED_TABS: ReadonlyArray<{
  readonly id: Exclude<ExtendedTabId, TeamDetailTabId>
  readonly label: string
}> = [
  { id: 'addons', label: 'Add-ons' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'payments', label: 'Payments' },
  { id: 'storage', label: 'Storage' },
  { id: 'consumption', label: 'Consumption' },
  { id: 'rate-limits', label: 'Rate limits' },
  { id: 'danger-zone', label: 'Danger zone' },
]

function ExtendedTabsBar({
  tab,
  onTabChange,
}: ExtendedTabsBarProps): ReactElement {
  return (
    <div
      role="tablist"
      aria-label="Extended team admin"
      className="ka-filter-group ka-filter-group--wrap"
    >
      {EXTENDED_TABS.map((spec) => {
        const isActive = tab === spec.id
        return (
          <button
            key={spec.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(spec.id)}
            className={`ka-detail-chip${isActive ? ' is-active' : ''}`}
          >
            {spec.label}
          </button>
        )
      })}
    </div>
  )
}

function PageMessage({
  title,
  body,
}: {
  title: string
  body: ReactNode
}): ReactElement {
  return (
    <div className="ka-page">
      <div role="alert" className="ka-card ka-empty">
        <div className="ka-empty-title">{title}</div>
        <p className="ka-empty-body">{body}</p>
        <p className="ka-empty-body">
          <Link to="/admin/teams">Return to teams list</Link>
        </p>
      </div>
    </div>
  )
}

function DetailSkeleton(): ReactElement {
  return (
    <div className="ka-page" aria-busy="true" aria-label="Loading team">
      <div className="ka-card ka-page-section ka-detail-sk-head">
        <div className="ka-sk ka-sk--avatar" />
        <div className="ka-detail-sk-head-body">
          <div className="ka-sk ka-sk--heading" />
          <div className="ka-sk ka-sk--body-xs ka-sk--mt" />
        </div>
      </div>
      <div className="ka-card ka-sk--card" aria-hidden="true" />
    </div>
  )
}
